/*
 * Copyright (C) 2021 Hal Perkins.  All rights reserved.  Permission is
 * hereby granted to students registered for University of Washington
 * CSE 331 for use solely during Winter Quarter 2021 for purposes of
 * the course.  No other use, copying, distribution, or modification
 * is permitted without prior written consent. Copyrights for
 * third-party components of this work must be honored.  Instructors
 * interested in reusing these course materials should contact the
 * author.
 */

import React, {Component} from 'react';

import {Button, Grid, MenuItem, Paper, Select, Switch} from "@material-ui/core";
import BuildingNames from "./util/BuildingNames";

interface SelectorProps {
    findLocation(): void;                   // callback function to find the user's location.
    buildingNames: BuildingNames | null,           // the JSON of the mapping of short to long names.
    draw(start: number, end: number): void; // callback function to draw the path.
    clear(): void;                          // callback function to clear the app's state.
    updateLocation(): void;     // callback to update the user's location
    stepByStep: boolean;        // indicates step-by-step navigation mode
    toggleStepByStep(): void;   // toggles step-by-step navigation mode
    step: number;               // the current step the navigation is on, if on
    max: number;                // the max step that can be incremented to
    stepForward(): void;        // callback to increment the step
    stepBackward(): void;       // callback to decrement the step
    panAndZoom: boolean;        // indicates pan and zoom view mode
    togglePanAndZoom(): void;   // toggles pan and zoom view mode
}

interface SelectorState {
    start: number;      // the JSON index of the start building.    -1 denotes no selected building.
    end: number;        // the JSON index of the end building.      -1 denotes no selected building.
}

class PathSelector extends Component<SelectorProps, SelectorState> {

    constructor(props: any) {
        super(props);
        this.state = {
            start: -1,
            end: -1,
        };
    }

    /**
     * Resets the state of the selector.
     */
    resetState = () => {
        this.setState({
            start: -1,
            end: -1
        });
    };

    /**
     * Handles when the start select menu is modified.
     *
     * @param event the React event triggered when the start select menu is modified.
     */
    handleStartChange = (event: React.ChangeEvent<{value: unknown}>) => {
        const start: number = event.target.value as number;
        if (start === -2) {
            this.props.findLocation();
        }
        this.setState({
            start: start
        });
    };

    /**
     * Handles when the end select menu is modified.
     *
     * @param event the React event triggered when the end select menu is modified.
     */
    handleEndChange = (event: React.ChangeEvent<{value: unknown}>) => {
        const end: number = event.target.value as number;
        this.setState({
            end: end
        });
    };

    /**
     * Handles when the draw button is pressed.
     */
    handleDrawClick = () => {
        this.props.draw(this.state.start, this.state.end);
    };

    /**
     * Handles when the clear button is pressed.
     */
    handleClearClick = () => {
        this.resetState();
        this.props.clear();
    };

    render() {
        let content;
        // will not let you use the select if there are no options (i.e. disconnected from server)
        if (this.props.buildingNames === null) {
            content =
                <div style={{padding: "10px"}}>
                    Connecting to server...
                </div>
        } else {
            const buildingNames: BuildingNames = this.props.buildingNames;
            const start: number = this.state.start;
            const end: number = this.state.end;
            const unselected: boolean = end === -1 || start === -1;

            const switchProps = {
                checked: this.props.stepByStep,
                onChange: this.props.toggleStepByStep,
            }
            const panAndZoomSwitchProps = {
                checked: this.props.panAndZoom,
                onChange: this.props.togglePanAndZoom,
            }
            content = (
                <div>
                    <Grid container style={{padding: "10px"}}>
                        <Grid item>
                            <Select
                                value={start}
                                onChange={this.handleStartChange}
                            >
                                <MenuItem
                                    value={-1}
                                >
                                    <em style={{color: "darkgray"}}>
                                        Start
                                    </em>
                                </MenuItem>
                                <MenuItem
                                    value={-2}
                                >
                                    <em style={{color: "green"}}>
                                        Current Location
                                    </em>
                                </MenuItem>
                                {
                                    Object.entries(buildingNames).map((value, i: number) => {
                                        return <MenuItem value={i} key={i}>{value[1]}</MenuItem>
                                    })
                                }
                            </Select>
                        </Grid>
                        <Grid item>
                            <Select
                                value={end}
                                onChange={this.handleEndChange}
                            >
                                <MenuItem
                                    value={-1}
                                >
                                    <em style={{color: "darkgray"}}>
                                        End
                                    </em>
                                </MenuItem>
                                {
                                    Object.entries(buildingNames).map((value, i: number) => {
                                        return <MenuItem value={i} key={i}>{value[1]}</MenuItem>
                                    })
                                }
                            </Select>
                        </Grid>
                        <Grid item>
                            <Button
                                onClick={this.props.updateLocation}
                            >
                                Update Location
                            </Button>
                        </Grid>
                    </Grid>
                    <p style={{margin: "10px"}}>
                        { unselected ?
                            "Select a starting point and an ending point to navigate."
                            :
                            "Preparing walking directions from " +
                            ( start === -2 ?
                                    "the building closest to your location "
                                    :
                                    Object.values(buildingNames)[start]
                            ) + " to " +
                            Object.values(buildingNames)[end] + "."
                        }
                    </p>
                    <Grid container>
                        <Grid item style={{padding: "10px"}}>
                            <Paper style={{padding: "5px"}}>
                                <Button
                                    disabled={unselected}
                                    onClick={this.handleDrawClick}
                                >
                                    Go!
                                </Button>
                                <Button
                                    onClick={this.handleClearClick}
                                >
                                    Clear
                                </Button>
                            </Paper>
                        </Grid>
                        <Grid item style={{padding: "10px"}}>
                            <Paper style={{padding: "5px"}}>
                                Move map automatically with path:
                                <Switch
                                    {...panAndZoomSwitchProps}
                                />
                            </Paper>
                        </Grid>
                        <Grid item style={{padding: "10px"}}>
                            <Paper style={{padding: "5px"}}>
                                Step-by-step mode:
                                <Switch
                                    {...switchProps}
                                />
                                <Button
                                    disabled={!this.props.stepByStep ||
                                    this.props.step <= 0}
                                    onClick={this.props.stepBackward}
                                >
                                    Previous Step
                                </Button>
                                <Button
                                    disabled={!this.props.stepByStep ||
                                    this.props.step >= this.props.max}
                                    onClick={this.props.stepForward}
                                >
                                    Next Step
                                </Button>
                            </Paper>
                        </Grid>
                    </Grid>
                </div>
            )
        }
        return (
            <Paper style={{width: "50%", margin: "auto"}}>
                <h1 style={{textAlign: "center", margin: "auto"}}>Campus Paths!</h1>
                {content}
            </Paper>
        );
    }

}

export default PathSelector;
